import { collection, doc, getDocs, addDoc, updateDoc, deleteDoc, query, getDoc, Timestamp } from "firebase/firestore";
import { db } from "./firebase";
import { COLLECTIONS } from "./collections";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { taskToFirestore } from "../models/task";

class TaskService {
    /**
     * Retorna a coleção de tarefas do Tenant
     */
    static getCollectionRef(idTenant) {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório");
        return collection(db, "tenants", idTenant, COLLECTIONS.TASKS || "tasks");
    }

    /**
     * Resgata as tarefas operacionais de um tenant, permitindo filtros simplificados.
     */
    static async getTasks(idTenant) {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório");
        
        try {
            const tasksRef = this.getCollectionRef(idTenant);
            // Poderíamos criar orderBys se tivessem índices. 
            // Como é um kanban operacional geral, consultamos todas ou limitadas por data futura se escalonar muito.
            const querySnapshot = await getDocs(query(tasksRef));
            
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error("Erro ao buscar tarefas:", error);
            throw error;
        }
    }

    /**
     * Cria uma nova Tarefa
     */
    static async createTask(idTenant, authUser, taskData) {
        if (!idTenant || !authUser || !taskData) {
            throw new Error("Parâmetros incompletos para acessar a camada de dados - Tasks");
        }

        try {
            const tenant = { idTenant };
            const preparedData = taskToFirestore(taskData, tenant, authUser);
            
            const tasksRef = this.getCollectionRef(idTenant);
            const newDoc = await addDoc(tasksRef, preparedData);

            await AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.TASKS,
                action: AUDIT_ACTIONS.CREATE,
                entityId: newDoc.id,
                entityName: preparedData.title,
                description: `Tarefa "${preparedData.title}" criada no status ${preparedData.status}.`,
                details: preparedData
            });

            return { id: newDoc.id, ...preparedData };
        } catch (error) {
            console.error("Erro ao criar Tarefa:", error);
            throw error;
        }
    }

    /**
     * Atualiza uma Tarefa
     */
    static async updateTask(idTenant, authUser, taskId, taskData, oldData) {
        if (!idTenant || !taskId || !taskData || !authUser) {
             throw new Error("Parâmetros incompletos para atualização de Tarefa");
        }

        try {
            const tenant = { idTenant };
            
            const preparedData = taskToFirestore(taskData, tenant, authUser);
            // Removendo id pois ele já existe e não se salva id dentro do body nativamente ou atrapalha merge.
            delete preparedData.idTenant; // já garantido na docRef

            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.TASKS || "tasks", taskId);
            
            await updateDoc(docRef, preparedData);

            if (oldData) {
               const changes = AuditService.calculateDiff(oldData, preparedData);
               if (changes) {
                   await AuditService.log({
                        idTenant,
                        user: authUser,
                        module: AUDIT_MODULES.TASKS,
                        action: AUDIT_ACTIONS.UPDATE,
                        entityId: taskId,
                        entityName: preparedData.title,
                        description: `Tarefa "${preparedData.title}" atualizada.`,
                        details: changes
                    });
               }
            }

            return preparedData;
        } catch (error) {
            console.error("Erro ao atualizar Tarefa:", error);
            throw error;
        }
    }

    /**
     * Update Task Status Only (Drag and Drop Otimizado)
     */
    static async updateTaskStatus(idTenant, authUser, taskId, newStatus) {
        if (!idTenant || !taskId || !newStatus || !authUser) {
            throw new Error("Parâmetros incompletos para atualização de Status da Tarefa");
        }

        try {
            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.TASKS || "tasks", taskId);
            const docSnap = await getDoc(docRef);
            
            if(!docSnap.exists()){
                throw new Error("A Tarefa não existe mais.");
            }

            const oldData = docSnap.data();

            await updateDoc(docRef, {
                status: newStatus,
                updatedAt: Timestamp.now(),
                updatedBy: { uid: authUser.uid, name: authUser.displayName || authUser.email || "Sistema" }
            });

            await AuditService.log({
                 idTenant,
                 user: authUser,
                 module: AUDIT_MODULES.TASKS,
                 action: AUDIT_ACTIONS.UPDATE,
                 entityId: taskId,
                 entityName: oldData.title,
                 description: `Status da tarefa "${oldData.title}" alterado para ${newStatus}.`,
                 details: { status: { from: oldData.status, to: newStatus } }
             });

            return true;
        } catch (error) {
           console.error("Erro ao atualizar status da tarefa:", error);
           throw error; 
        }
    }

    /**
     * Remove uma Tarefa
     */
    static async deleteTask(idTenant, authUser, taskId, taskName) {
        if (!idTenant || !taskId || !authUser) {
             throw new Error("Parâmetros incompletos para excluir uma Tarefa");
        }

        try {
            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.TASKS || "tasks", taskId);
            await deleteDoc(docRef);

            await AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.TASKS,
                action: AUDIT_ACTIONS.DELETE,
                entityId: taskId,
                entityName: taskName || 'Tarefa Desconhecida',
                description: `Tarefa "${taskName || taskId}" excluída.`
            });

            return true;
        } catch (error) {
            console.error("Erro ao excluir Tarefa:", error);
            throw error;
        }
    }
}

export default TaskService;
